import sodium from "libsodium-wrappers-sumo";
import type { EncryptedMessage } from "./ratchet.js";

// --- Encoding helpers ---

export function hexToBytes(hex: string): Uint8Array {
  return sodium.from_hex(hex);
}

export function bytesToHex(bytes: Uint8Array): string {
  return sodium.to_hex(bytes);
}

export function base64ToBytes(b64: string): Uint8Array {
  return sodium.from_base64(b64, sodium.base64_variants.ORIGINAL);
}

export function bytesToBase64(bytes: Uint8Array): string {
  return sodium.to_base64(bytes, sodium.base64_variants.ORIGINAL);
}

// --- Base64 transport format (owner-agent) ---
// Must match packages/web/lib/crypto-bridge.ts exactly

export interface TransportMessage {
  header_blob: string; // base64-encoded JSON with binary fields as base64
  ciphertext: string; // base64-encoded raw ciphertext
}

export function encryptedMessageToTransport(
  msg: EncryptedMessage,
): TransportMessage {
  const headerObj = {
    dhPublicKey: bytesToBase64(msg.header.dhPublicKey),
    previousChainLength: msg.header.previousChainLength,
    messageNumber: msg.header.messageNumber,
    headerSignature: bytesToBase64(msg.headerSignature),
    nonce: bytesToBase64(msg.nonce),
  };
  const headerJson = JSON.stringify(headerObj);
  const headerBlob = bytesToBase64(
    new TextEncoder().encode(headerJson),
  );
  return {
    header_blob: headerBlob,
    ciphertext: bytesToBase64(msg.ciphertext),
  };
}

export function transportToEncryptedMessage(
  transport: TransportMessage,
): EncryptedMessage {
  const headerJson = new TextDecoder().decode(
    base64ToBytes(transport.header_blob),
  );
  const headerObj = JSON.parse(headerJson);
  return {
    header: {
      dhPublicKey: base64ToBytes(headerObj.dhPublicKey),
      previousChainLength: headerObj.previousChainLength,
      messageNumber: headerObj.messageNumber,
    },
    headerSignature: base64ToBytes(headerObj.headerSignature),
    nonce: base64ToBytes(headerObj.nonce),
    ciphertext: base64ToBytes(transport.ciphertext),
  };
}

// --- V2 transport format (header encryption) ---

export interface TransportMessageV2 {
  envelope_version: "2.0.0";
  encrypted_header: string; // base64
  header_nonce: string; // base64
  header_signature: string; // base64
  nonce: string; // base64
  ciphertext: string; // base64
}

export function encryptedMessageToTransportV2(
  msg: EncryptedMessage,
): TransportMessageV2 {
  if (!msg.encryptedHeader || !msg.headerNonce) {
    throw new Error("encryptedMessageToTransportV2 requires v2 encrypted message");
  }
  return {
    envelope_version: "2.0.0",
    encrypted_header: bytesToBase64(msg.encryptedHeader),
    header_nonce: bytesToBase64(msg.headerNonce),
    header_signature: bytesToBase64(msg.headerSignature),
    nonce: bytesToBase64(msg.nonce),
    ciphertext: bytesToBase64(msg.ciphertext),
  };
}

/**
 * Decode a V2 transport message. The header stays encrypted —
 * the DoubleRatchet.decrypt() method decrypts it using the header key.
 *
 * We still need to populate the plaintext `header` field for the ratchet
 * to identify the DH public key and advance the chain. For V2, the caller
 * must provide the plaintext header separately (from the ratchet's decrypt path).
 *
 * In practice, the receiver will first try to parse as V2, then fall through
 * to ratchet.decrypt() which handles header decryption internally.
 */
export function transportV2ToEncryptedMessage(
  transport: TransportMessageV2,
  plaintextHeader: EncryptedMessage["header"],
): EncryptedMessage {
  return {
    header: plaintextHeader,
    headerSignature: base64ToBytes(transport.header_signature),
    ciphertext: base64ToBytes(transport.ciphertext),
    nonce: base64ToBytes(transport.nonce),
    envelopeVersion: "2.0.0",
    encryptedHeader: base64ToBytes(transport.encrypted_header),
    headerNonce: base64ToBytes(transport.header_nonce),
  };
}

/**
 * Encode a V2 transport message that also includes the plaintext header
 * (for the server to relay to the ratchet). This is the practical format:
 * the server stores the encrypted header but relays both encrypted and
 * plaintext header_blob so the receiver can reconstruct the EncryptedMessage.
 */
export function encryptedMessageToTransportV2Full(
  msg: EncryptedMessage,
): TransportMessage & { envelope_version: "2.0.0"; encrypted_header: string; header_nonce: string } {
  if (!msg.encryptedHeader || !msg.headerNonce) {
    throw new Error("encryptedMessageToTransportV2Full requires v2 encrypted message");
  }
  const v1 = encryptedMessageToTransport(msg);
  return {
    ...v1,
    envelope_version: "2.0.0",
    encrypted_header: bytesToBase64(msg.encryptedHeader),
    header_nonce: bytesToBase64(msg.headerNonce),
  };
}

// --- Hex transport format (A2A channels) ---

export interface HexTransportMessage {
  header_blob: string; // hex-encoded JSON with dhPublicKey as hex
  header_signature: string; // hex of headerSignature bytes
  ciphertext: string; // hex of ciphertext bytes
  nonce: string; // hex of nonce bytes
}

export function encryptedMessageToHexTransport(
  msg: EncryptedMessage,
): HexTransportMessage {
  const headerObj = {
    dhPublicKey: bytesToHex(msg.header.dhPublicKey),
    previousChainLength: msg.header.previousChainLength,
    messageNumber: msg.header.messageNumber,
  };
  const headerBlobHex = Buffer.from(JSON.stringify(headerObj)).toString("hex");
  return {
    header_blob: headerBlobHex,
    header_signature: bytesToHex(msg.headerSignature),
    ciphertext: bytesToHex(msg.ciphertext),
    nonce: bytesToHex(msg.nonce),
  };
}

export function hexTransportToEncryptedMessage(
  transport: HexTransportMessage,
): EncryptedMessage {
  const headerJson = Buffer.from(transport.header_blob, "hex").toString("utf-8");
  const headerObj = JSON.parse(headerJson);
  return {
    header: {
      dhPublicKey: hexToBytes(headerObj.dhPublicKey),
      previousChainLength: headerObj.previousChainLength,
      messageNumber: headerObj.messageNumber,
    },
    headerSignature: hexToBytes(transport.header_signature),
    ciphertext: hexToBytes(transport.ciphertext),
    nonce: hexToBytes(transport.nonce),
  };
}
